import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Field, Stat, StatusBadge, Textarea } from "@/components/UI";
import { Plus, Trash, Coin, Receipt, CheckCircle, Warning, PencilSimple, Clock, UserCircle, Calendar as CalendarIcon, ChartPieSlice, Leaf, CaretRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATS, PARTY_TYPES, PAYMENT_TYPES, LEAVE_REASONS, MONTHS_TR, today, fmt } from "./constants";

export function FeedAnalysisTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    api.get(`/finance/feed-analysis${qs}`)
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.message || "Analiz alınamadı"))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only; tarih filtresi "Uygula" ile elle yüklenir
  }, []);

  const applyFilter = () => load();
  const clearFilter = () => { setStartDate(""); setEndDate(""); setTimeout(load, 0); };

  const s = data?.summary || {};
  const totalFeed = Number(s.total_feed_expense || 0);
  const ownShare = totalFeed > 0 ? (Number(s.own_produced_market_value || 0) / totalFeed) * 100 : 0;
  const purchasedShare = totalFeed > 0 ? (Number(s.purchased_feed_expense || 0) / totalFeed) * 100 : 0;
  const savings = Number(s.farm_profit_savings || 0);
  const savingsTone = savings >= 0 ? "ok" : "danger";

  return (
    <div data-testid="feed-analysis-tab">
      {/* Açıklama başlığı */}
      <Card className="mb-4" data-testid="feed-analysis-intro">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#E4EEE6] rounded-md">
            <ChartPieSlice size={22} weight="duotone" className="text-[#2D4A36]" />
          </div>
          <div className="flex-1">
            <h3 className="heading text-lg mb-1">Yem Gideri Ayrımı — Piyasadan Alınan vs Kendi Üretimimiz</h3>
            <p className="text-sm text-[#5e675e]">
              Kendi ürettiğiniz kaba yemleri (silaj, yonca vb.) <b>Rasyon "Bugün Yemle"</b> veya <b>Lely</b> ile
              yedirdiğinizde, tüketim <b>piyasa fiyatı üzerinden</b> gider yazılır. Sistem, aynı ürünün
              <b> gerçek tarla üretim maliyeti</b> (sulama, gübre, işçilik) ile piyasa değeri arasındaki farkı
              <b className="text-[#2D4A36]"> "Tarla Kârı / İşletme Tasarrufu"</b> olarak raporlar.
              <span className="block mt-1 text-xs text-[#8a938c]">Not: Stok Sayımı artık tüketim yazmaz — yalnızca fire/fazla mutabakatı içindir ve bu rapora dahil edilmez.</span>
            </p>
          </div>
        </div>
      </Card>

      {/* Tarih filtresi */}
      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Başlangıç">
            <Input type="date" data-testid="feed-start-date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </Field>
          <Field label="Bitiş">
            <Input type="date" data-testid="feed-end-date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </Field>
          <Button type="button" data-testid="feed-apply-filter" onClick={applyFilter}>Uygula</Button>
          <Button type="button" onClick={clearFilter} className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">Tümü</Button>
          <span className="text-xs text-[#8a938c] ml-auto">
            {startDate || endDate ? `${startDate || "başlangıç"} → ${endDate || "bugün"}` : "Tüm zamanlar"}
          </span>
        </div>
      </Card>

      {loading ? (
        <Card><div className="text-center py-8 text-[#8a938c]">Yükleniyor...</div></Card>
      ) : (
        <>
          {/* KPI'lar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="feed-kpis">
            <Stat testId="feed-kpi-total" label="Toplam Yem Gideri" value={fmt(s.total_feed_expense)} unit="₺" tone="accent" />
            <Stat testId="feed-kpi-purchased" label="Piyasadan Alınan" value={fmt(s.purchased_feed_expense)} unit="₺" tone="warn" />
            <Stat testId="feed-kpi-own" label="Kendi Üretimimiz (Piyasa)" value={fmt(s.own_produced_market_value)} unit="₺" tone="ok" />
            <Stat testId="feed-kpi-field-cost" label="Gerçek Tarla Maliyeti" value={fmt(s.own_produced_field_cost_allocated)} unit="₺" tone="warn" />
            <Stat testId="feed-kpi-savings" label="Tarla Kârı / Tasarruf" value={fmt(s.farm_profit_savings)} unit="₺" tone={savingsTone} />
          </div>

          {/* Dağılım barı */}
          {totalFeed > 0 && (
            <Card className="mb-4" data-testid="feed-split-card">
              <div className="flex items-center justify-between mb-2">
                <h4 className="heading text-base">Toplam Yem Gideri Dağılımı</h4>
                <div className="text-xs text-[#8a938c]">
                  <b className="text-[#2D4A36]">%{s.own_produced_share_pct ?? 0}</b> kendi üretimimiz ·
                  <b className="text-[#C45B45] ml-1">%{Math.round(purchasedShare * 10) / 10}</b> dışarıdan
                </div>
              </div>
              <div className="flex h-8 rounded-md overflow-hidden border border-[#E5E2DC]">
                <div className="bg-[#2D4A36] text-white text-xs flex items-center justify-center font-semibold transition-all"
                     style={{ width: `${ownShare}%` }}
                     data-testid="feed-split-own">
                  {ownShare > 12 && `${fmt(s.own_produced_market_value)} ₺`}
                </div>
                <div className="bg-[#C45B45] text-white text-xs flex items-center justify-center font-semibold transition-all"
                     style={{ width: `${purchasedShare}%` }}
                     data-testid="feed-split-purchased">
                  {purchasedShare > 12 && `${fmt(s.purchased_feed_expense)} ₺`}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-[#5e675e]">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-[#2D4A36] rounded-sm inline-block"></span>
                  Kendi Üretimimiz <b className="text-[#2D4A36]">{fmt(s.own_produced_market_value)} ₺</b>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-[#C45B45] rounded-sm inline-block"></span>
                  Piyasadan Alınan <b className="text-[#C45B45]">{fmt(s.purchased_feed_expense)} ₺</b>
                </span>
              </div>
            </Card>
          )}

          {/* Ürün bazlı detay tablo */}
          <Card className="!p-0 overflow-hidden" data-testid="feed-crop-breakdown">
            <div className="p-4 pb-3 border-b border-[#E5E2DC]">
              <h4 className="heading text-lg">Ürün Bazlı Tarla Kârı Analizi</h4>
              <p className="text-xs text-[#8a938c] mt-0.5">
                Her kendi üretiminizde yem için: kaç ton yedirdiniz, piyasa değeri nedir, gerçek tarla maliyeti nedir,
                farkı ne kadar tasarruf sağladı?
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ürün</th>
                    <th className="text-right">Alan</th>
                    <th className="text-right">Toplam Hasat</th>
                    <th className="text-right">Tüketilen (Piyasa Değeri)</th>
                    <th className="text-right">Kullanım %</th>
                    <th className="text-right">Tarla Maliyeti (Orantılı)</th>
                    <th className="text-right">Tarla Kârı</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.by_crop || []).map(c => {
                    const sav = Number(c.savings || 0);
                    const savColor = sav >= 0 ? "text-[#2D4A36]" : "text-[#C45B45]";
                    return (
                      <tr key={c.crop_id} data-testid={`feed-crop-row-${c.crop_id}`}>
                        <td className="font-semibold">
                          <div className="flex items-center gap-1.5">
                            <Leaf size={14} weight="fill" className="text-[#2D4A36]" />
                            {c.crop_name}
                          </div>
                        </td>
                        <td className="text-right tabnum">
                          {c.area_dekar ? <><b>{fmt(c.area_dekar)}</b> <span className="text-xs text-[#8a938c]">dekar</span></> : <span className="text-xs text-[#8a938c]">—</span>}
                        </td>
                        <td className="text-right tabnum">{fmt(c.total_harvest)} <span className="text-xs text-[#8a938c]">{c.unit}</span></td>
                        <td className="text-right tabnum font-semibold text-[#DDA74F]">{fmt(c.consumed_market_value)} <span className="text-xs text-[#8a938c] font-normal">₺</span></td>
                        <td className="text-right tabnum">
                          <span className="inline-block px-2 py-0.5 rounded bg-[#F5F2EC] text-xs font-semibold">%{c.allocation_ratio_pct}</span>
                        </td>
                        <td className="text-right tabnum text-[#C45B45]">{fmt(c.field_cost_allocated)} ₺</td>
                        <td className={`text-right tabnum font-bold ${savColor}`} data-testid={`feed-crop-savings-${c.crop_id}`}>
                          {sav >= 0 ? "+" : ""}{fmt(sav)} ₺
                        </td>
                      </tr>
                    );
                  })}
                  {!(data?.by_crop || []).length && (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-[#8a938c]">
                        <Leaf size={32} weight="duotone" className="mx-auto mb-2 opacity-40" />
                        Henüz kendi üretimimiz olarak etiketlenmiş yem tüketimi yok.<br/>
                        <span className="text-xs">Tarla modülünden hasat girip, o yemi Rasyon "Bugün Yemle" veya Lely ile yedirdiğinizde burada görünecek.</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Nasıl hesaplandığı */}
          <Card className="mt-4 !bg-[#F5F2EC] !border-[#E5E2DC]" data-testid="feed-help-card">
            <h4 className="heading text-sm mb-2 flex items-center gap-1">
              <ChartPieSlice size={16} weight="duotone" /> Hesaplama Nasıl Yapılır?
            </h4>
            <div className="text-xs text-[#5e675e] space-y-1.5">
              <div><b>1.</b> Tarla modülüne bir ürün ekleyip (ör: Mısır Silajı, 40 dekar) tarla giderlerini (sulama, gübre, işçilik...) girin.</div>
              <div><b>2.</b> Hasat sırasında miktar × <b>piyasa fiyatı</b> (ör: 20 ton × 500 ₺) girersiniz. Bu miktar Stok'a "kaba yem" olarak eklenir ve <b className="text-[#2D4A36]">"Kendi Üretimimiz"</b> etiketiyle işaretlenir.</div>
              <div><b>3.</b> Bu yemi <b>Rasyon "Bugün Yemle"</b> ile yedirdiğinizde (veya <b>Lely</b> tüketimi girdiğinizde), yedirilen miktar × piyasa fiyatı → Finans'a <b>"Yem Gideri"</b> olarak yazılır (piyasadan almış gibi). Bu gider "Kendi Üretimimiz" rozetiyle görünür. <span className="text-[#8a938c]">Stok Sayımı tüketim yazmaz.</span></div>
              <div><b>4.</b> Sistem, tüketilen <b>piyasa değeri oranında</b> gerçek tarla maliyetini hesaplar (ör: 10.000 ₺'lik hasadın 4.000 ₺'sini yedik = %40 → 8.000 ₺ tarla giderinin %40'ı = 3.200 ₺).</div>
              <div><b>5.</b> <b>Tarla Kârı = Piyasa Değeri − Gerçek Tarla Maliyeti</b> (ör: 4000 − 3200 = <b className="text-[#2D4A36]">800 ₺ tasarruf</b>). Bu, kendi ürettiğinizde piyasadan almaya kıyasla ne kadar avantaj sağladığınızı gösterir.</div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
